import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { onUnauthorized } from "../api/authEvents";
import { useApiClient } from "../api/ApiProvider";
import type { ApiClient, Session } from "../api/client";
import { isServiceUnavailable } from "../api/errors";
import { clearCachedSession, readCachedSession, writeCachedSession } from "../api/sessionCache";

/** Small wait between retries below — real time, not simulated (the thing
 * being waited out, `auth.Service`'s bounded Argon2id slot pool, clears on
 * its own real-world timescale as concurrent logins finish). */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Login attempts before giving up on a run of 503s: 1 initial + 2 retries.
 * The server's hashing-slot pool (`defaultHashConcurrency`, currently 4) is
 * sized for steady-state traffic, not a burst of near-simultaneous sign-ins
 * (e.g. several operators' browsers restoring at once, or — the case that
 * surfaced this — a fully-parallel E2E suite logging in as the same account
 * from several workers at once); a slot frees up in well under a second, so
 * three attempts spread over ~450ms clears a transient burst without turning
 * a real outage into a long hang.
 */
const LOGIN_BUSY_RETRIES = 2;
const LOGIN_BUSY_RETRY_DELAY_MS = 150;

/**
 * Calls `client.login` and retries ONLY a 503 ("server busy, retry shortly" —
 * see `isServiceUnavailable`'s doc comment): that status is the server's own
 * signal that the request itself was fine and shedding was transient, unlike
 * a 401 (wrong credentials) or anything else, which must surface to the
 * operator on the first attempt, not silently retry a few hundred ms later.
 */
async function loginWithBusyRetry(client: ApiClient, username: string, password: string): Promise<Session> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await client.login({ username, password });
    } catch (err) {
      if (!isServiceUnavailable(err) || attempt >= LOGIN_BUSY_RETRIES) throw err;
      await delay(LOGIN_BUSY_RETRY_DELAY_MS * (attempt + 1));
    }
  }
}

interface AuthContextValue {
  session: Session | null;
  isAuthenticating: boolean;
  authError: string | null;
  login: (username: string, password: string) => Promise<void>;
  adoptSession: (s: Session) => void;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Real auth (charter C-3 local bootstrap; C-5 OIDC/SSO later) sets a
 * server-side httpOnly session cookie on `login()` — the browser/this code
 * never holds a bearer credential. `session` here is just the non-secret
 * profile the server returned (userId/displayName/role), restored from
 * `sessionCache` on mount so a reload doesn't force a needless re-login: the
 * real backend has no `GET /session`/`/me` endpoint yet to recover "who is
 * this" from the cookie alone (see sessionCache.ts for the tradeoff). This
 * state is never authoritative — the server enforces auth on every request
 * regardless of what it says — and any 401 from an authenticated call (see
 * authEvents.ts) clears it, which sends `ProtectedRoute` back to `/login`
 * reactively.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useApiClient();
  const [session, setSession] = useState<Session | null>(() => readCachedSession());
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  useEffect(
    () =>
      onUnauthorized(() => {
        setSession(null);
        clearCachedSession();
      }),
    [],
  );

  // Session-recovery bootstrap (ADR-0037 PR-2b): runs ONCE, only when there is
  // no cached session to start from. This is what lets a session outlive a
  // FULL PAGE NAVIGATION with no login()/register() call of its own — the one
  // and only way an OIDC/SSO sign-in completes (the IdP round trip is a
  // top-level browser redirect the SPA cannot intercept or await; see
  // routes/LoginPage.tsx / routes/SSORedirectPage.tsx). Without this, a
  // successful SSO login would land the browser on "/" with a valid session
  // cookie but nothing in this tab's memory, and ProtectedRoute would bounce
  // it straight back to /login.
  //
  // client.getSession() is DELIBERATELY forgiving: it resolves null (never
  // throws) whenever there's nothing to recover, including — as of this PR —
  // every call, because the server route it assumes does not exist yet (see
  // ApiClient.getSession's doc comment). That is a real, flagged gap: until
  // the server adds it, this effect is a no-op and SSO login cannot complete
  // in the SPA. It costs nothing to have this wired ahead of that landing,
  // and it also quietly improves the ordinary password case — a brand-new
  // tab opened against an already-authenticated cookie (no sessionStorage of
  // its own) recovers instead of showing /login for no reason.
  useEffect(() => {
    if (session) return;
    let cancelled = false;
    void (async () => {
      const recovered = await client.getSession().catch(() => null);
      if (cancelled || !recovered) return;
      setSession(recovered);
      writeCachedSession(recovered);
    })();
    return () => {
      cancelled = true;
    };
    // Intentionally runs only on mount (and again if the client identity
    // itself changes) — it must NOT re-fire every time `session` is cleared
    // by a 401, or a genuinely expired session would loop retrying recovery
    // forever instead of settling on /login.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const login = useCallback(
    async (username: string, password: string) => {
      setIsAuthenticating(true);
      setAuthError(null);
      try {
        const result = await loginWithBusyRetry(client, username, password);
        setSession(result);
        writeCachedSession(result);
      } catch (err) {
        setAuthError(err instanceof Error ? err.message : "Sign-in failed. Try again.");
        throw err;
      } finally {
        setIsAuthenticating(false);
      }
    },
    [client],
  );

  const adoptSession = useCallback((s: Session) => {
    setSession(s);
    writeCachedSession(s);
  }, []);

  const logout = useCallback(async () => {
    await client.logout();
    setSession(null);
    clearCachedSession();
  }, [client]);

  const value = useMemo(
    () => ({ session, isAuthenticating, authError, login, adoptSession, logout }),
    [session, isAuthenticating, authError, login, adoptSession, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
